import React, { useState, useRef } from 'react';
import { Menu, X, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { servicesData } from '@/data/servicesData';
import { useCustomImages } from '@/context/ImageContext';

const Header = () => {
  const { getImage } = useCustomImages();
  const headerLogo = getImage('headerLogo', '/logo.png');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsServicesOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
    }, 200);
  };

  const handleNavigation = (path, sectionId) => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
    
    if (path.startsWith('/#')) {
      if (location.pathname === '/') {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  const handleSubcategoryClick = (path) => {
    setIsServicesOpen(false);
    setIsMenuOpen(false);
    navigate(path);
    window.scrollTo(0, 0);
  };

  const navItems = [
    { name: 'Home', path: '/', isScroll: true, id: 'home' },
    { name: 'Services', path: '/services', isDropdown: true },
    { name: "Founder's Cut", path: '/founder', isScroll: false },
    { name: 'Blogs', path: '/blogs', isScroll: false },
    { name: 'Calculator', path: '/#calculator', isScroll: true, id: 'calculator' },
    { name: 'Contact', path: '/#contact', isScroll: true, id: 'contact' }
  ];

  return (
    <header className="sticky top-0 z-50 w-full glass-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div 
            className="flex items-center gap-0.5 sm:gap-0.5 cursor-pointer group" 
            onClick={() => navigate('/')}
          >
            <img 
              src={headerLogo} 
              alt="Stellars Estates Logo" 
              className="h-12 sm:h-14 md:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              loading="eager"
              decoding="async"
            />
            <span className="text-white font-extrabold text-xl sm:text-2xl tracking-tight font-heading">
              Stellars <span className="text-[#F3453F]">Estates</span>
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => {
              if (item.isDropdown) {
                const isActive = location.pathname.startsWith('/services');
                return (
                  <div
                    key={item.name}
                    className="relative group py-6"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      onClick={() => handleNavigation('/services')}
                      className={`flex items-center gap-1 text-sm font-semibold tracking-wide transition-colors duration-300 font-sans ${
                        isActive ? 'text-[#F3453F]' : 'text-white hover:text-[#F3453F]'
                      }`}
                    >
                      {item.name}
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform duration-300 ${isServicesOpen ? 'rotate-180 text-[#F3453F]' : ''}`} 
                      />
                      {isActive && (
                        <span className="absolute bottom-4 left-0 w-full h-0.5 bg-[#F3453F] rounded-full" />
                      )}
                    </button>

                    {/* Mega Dropdown Menu */}
                    <AnimatePresence>
                      {isServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 12, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.98 }}
                          transition={{ duration: 0.2 }}
                          className="fixed top-20 left-1/2 -translate-x-1/2 w-[95vw] max-w-7xl bg-[#111726]/98 backdrop-blur-2xl border border-white/15 rounded-2xl shadow-2xl p-6 lg:p-8 z-50 text-left overflow-y-auto max-h-[82vh]"
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        >
                          <div className="flex items-center justify-between pb-4 mb-6 border-b border-white/10">
                            <div>
                              <p className="text-lg font-bold text-white font-heading">Our Comprehensive Services</p>
                              <p className="text-xs text-white/90">Select any subcategory to view specialized solutions</p>
                            </div>
                            <button
                              onClick={() => handleNavigation('/services')}
                              className="text-xs font-semibold text-[#F3453F] hover:text-white flex items-center gap-1.5 transition-colors bg-[#F3453F]/10 hover:bg-[#F3453F] px-3 py-2 rounded-lg border border-[#F3453F]/30 min-h-[44px]"
                            >
                              Explore All Services
                              <ArrowRight size={14} />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {servicesData.map((category) => {
                              const CategoryIcon = category.icon;
                              return (
                                <div 
                                  key={category.id} 
                                  className="bg-white/[0.03] border border-white/10 rounded-xl p-4 hover:border-[#F3453F]/40 transition-all group/card"
                                >
                                  {/* Category Title */}
                                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
                                    <div className="p-1.5 rounded-lg bg-[#F3453F]/20 text-[#F3453F]">
                                      <CategoryIcon size={18} />
                                    </div>
                                    <p className="text-sm font-bold text-white font-heading group-hover/card:text-[#F3453F] transition-colors">
                                      {category.title}
                                    </p>
                                  </div>

                                  {/* Render Subgroups or Direct Subcategories */}
                                  {category.hasSubgroups ? (
                                    <div className="space-y-3">
                                      {category.groups.map((group) => (
                                        <div key={group.name} className="space-y-1">
                                          <div className="text-[11px] font-extrabold text-[#F3453F] uppercase tracking-wider pl-1 mb-1">
                                            {group.name}
                                          </div>
                                          <div className="space-y-1">
                                            {group.items.map((subItem) => (
                                              <button
                                                key={subItem.name}
                                                onClick={() => handleSubcategoryClick(subItem.path)}
                                                className="w-full text-left text-xs text-white/80 hover:text-white hover:bg-[#F3453F]/20 px-2 py-1.5 rounded transition-all flex items-center justify-between group/sub"
                                              >
                                                <span>{subItem.name}</span>
                                                <ChevronRight size={12} className="opacity-0 group-hover/sub:opacity-100 text-[#F3453F] transition-opacity" />
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="space-y-1">
                                      {category.items.map((subItem) => (
                                        <button
                                          key={subItem.name}
                                          onClick={() => handleSubcategoryClick(subItem.path)}
                                          className="w-full text-left text-xs text-white/80 hover:text-white hover:bg-[#F3453F]/20 px-2 py-1.5 rounded transition-all flex items-center justify-between group/sub"
                                        >
                                          <span>{subItem.name}</span>
                                          <ChevronRight size={12} className="opacity-0 group-hover/sub:opacity-100 text-[#F3453F] transition-opacity" />
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              const isActive = location.pathname === item.path || (item.path === '/' && location.pathname === '/' && !location.hash);
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.path, item.id)}
                  className={`relative text-sm font-semibold tracking-wide transition-colors duration-300 font-sans py-1 ${
                    isActive ? 'text-[#F3453F]' : 'text-white hover:text-[#F3453F]'
                  }`}
                >
                  {item.name}
                  {isActive && (
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#F3453F] rounded-full" />
                  )}
                </button>
              );
            })}
            <button
              onClick={() => handleNavigation('/#contact', 'contact')}
              className="btn-glass btn-glass-nav px-5 py-2.5 text-sm"
            >
              Get Free Consultation
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden btn-glass btn-glass-outline p-2 focus:outline-none"
            aria-label="Toggle Navigation Menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-y-auto max-h-[85vh] bg-[#141A29] border-t border-white/10"
          >
            <nav className="px-4 py-6 space-y-3">
              {/* Home */}
              <button
                onClick={() => handleNavigation('/', 'home')}
                className="block w-full text-left text-white hover:text-[#F3453F] font-medium py-2 px-3 rounded-md hover:bg-white/10"
              >
                Home
              </button>

              {/* Mobile Services Accordion */}
              <div className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02]">
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="w-full flex items-center justify-between text-left text-white font-semibold py-3 px-4 bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <span>Services</span>
                  <ChevronDown size={18} className={`transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180 text-[#F3453F]' : ''}`} />
                </button>

                <AnimatePresence>
                  {mobileServicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="px-3 py-2 space-y-3 bg-[#0F1420]"
                    >
                      <button
                        onClick={() => handleNavigation('/services')}
                        className="w-full text-left text-xs font-bold text-[#F3453F] py-2 px-3 rounded bg-[#F3453F]/10 flex items-center justify-between"
                      >
                        <span>View All Services</span>
                        <ArrowRight size={14} />
                      </button>

                      {servicesData.map((category) => {
                        const isCatOpen = mobileCategoryOpen === category.id;
                        return (
                          <div key={category.id} className="border-b border-white/5 pb-2">
                            <button
                              onClick={() => setMobileCategoryOpen(isCatOpen ? null : category.id)}
                              className="w-full flex items-center justify-between text-left text-xs font-bold text-white/90 py-1.5 px-2 hover:text-[#F3453F]"
                            >
                              <span>{category.title}</span>
                              <ChevronDown size={14} className={`transition-transform duration-200 ${isCatOpen ? 'rotate-180 text-[#F3453F]' : 'opacity-60'}`} />
                            </button>

                            {isCatOpen && (
                              <div className="pl-3 mt-1 space-y-2 border-l border-[#F3453F]/30 ml-2">
                                {category.hasSubgroups ? (
                                  category.groups.map((group) => (
                                    <div key={group.name} className="space-y-1">
                                      <span className="text-[10px] font-extrabold text-[#F3453F] uppercase tracking-wider block mt-1">
                                        {group.name}
                                      </span>
                                      {group.items.map((subItem) => (
                                        <button
                                          key={subItem.name}
                                          onClick={() => handleSubcategoryClick(subItem.path)}
                                          className="block w-full text-left text-xs text-white/70 hover:text-white py-1 px-2 rounded hover:bg-white/10"
                                        >
                                          {subItem.name}
                                        </button>
                                      ))}
                                    </div>
                                  ))
                                ) : (
                                  category.items.map((subItem) => (
                                    <button
                                      key={subItem.name}
                                      onClick={() => handleSubcategoryClick(subItem.path)}
                                      className="block w-full text-left text-xs text-white/70 hover:text-white py-1 px-2 rounded hover:bg-white/10"
                                    >
                                      {subItem.name}
                                    </button>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Founder's Cut */}
              <button
                onClick={() => handleNavigation('/founder')}
                className="block w-full text-left text-white hover:text-[#F3453F] font-medium py-2 px-3 rounded-md hover:bg-white/10"
              >
                Founder's Cut
              </button>

              {/* Blogs */}
              <button
                onClick={() => handleNavigation('/blogs')}
                className="block w-full text-left text-white hover:text-[#F3453F] font-medium py-2 px-3 rounded-md hover:bg-white/10"
              >
                Blogs
              </button>

              {/* Calculator */}
              <button
                onClick={() => handleNavigation('/#calculator', 'calculator')}
                className="block w-full text-left text-white hover:text-[#F3453F] font-medium py-2 px-3 rounded-md hover:bg-white/10"
              >
                Calculator
              </button>

              {/* Contact */}
              <button
                onClick={() => handleNavigation('/#contact', 'contact')}
                className="block w-full text-left text-white hover:text-[#F3453F] font-medium py-2 px-3 rounded-md hover:bg-white/10"
              >
                Contact
              </button>

              <div className="pt-2">
                <button
                  onClick={() => handleNavigation('/#contact', 'contact')}
                  className="w-full btn-glass btn-glass-nav py-3 text-center"
                >
                  Get Free Consultation
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
